import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, FileText, File as FilePdf, Download, Search, ArrowUp, ArrowDown, UserCog, RefreshCw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import { getActiveSportsYear, getSportsYears } from '../../services/sportsYearService';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';

type ReportType = 'payments' | 'registrations' | 'results' | 'refereeAssignments';
type SortDirection = 'asc' | 'desc';

interface SortConfig {
  key: string;
  direction: SortDirection;
}

const ReportsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [selectedReport, setSelectedReport] = useState<ReportType>('payments');
  const [reportData, setReportData] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  
  // New filters for results
  const [categories, setCategories] = useState<any[]>([]);
  const [ageGroups, setAgeGroups] = useState<any[]>([]);
  const [competitionYears, setCompetitionYears] = useState<number[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState<string>('All');
  const [selectedYear, setSelectedYear] = useState<string>('All');

  useEffect(() => {
    if (!isAuthorized) return;
    (async () => {
      try {
        const [years, active] = await Promise.all([
          getSportsYears().catch(() => []),
          getActiveSportsYear().catch(() => null),
        ]);
        if (years.length > 0) {
          setCompetitionYears((prev) => {
            const merged = Array.from(new Set([...years.map((y) => y.year), ...prev]));
            return merged.sort((a, b) => b - a);
          });
        }
        if (active) {
          setSelectedYear(active.toString());
        }
      } catch (err) {
        console.error('Error loading sports years:', err);
      }
    })();
  }, [isAuthorized]);

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      loadReportData();
    }
  }, [selectedReport, isAuthorized]);

  useEffect(() => {
    if (isAuthorized && selectedReport === 'results') {
      loadCategories();
      loadAgeGroups();
      
      // Set up real-time subscription for results
      const resultsSubscription = supabase
        .channel('results_changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'competition_results' 
        }, () => {
          console.log('Results table changed, refreshing data...');
          loadReportData();
        })
        .subscribe();
        
      // Set up real-time subscription for team results
      const teamResultsSubscription = supabase
        .channel('team_results_changes')
        .on('postgres_changes', { 
          event: '*', 
          schema: 'public', 
          table: 'team_results' 
        }, () => {
          console.log('Team results table changed, refreshing data...');
          loadReportData();
        })
        .subscribe();
      
      // Clean up subscriptions on unmount or when changing report type
      return () => {
        resultsSubscription.unsubscribe();
        teamResultsSubscription.unsubscribe();
      };
    }
  }, [isAuthorized, selectedReport]);

  // Set up real-time subscriptions for other report types
  useEffect(() => {
    if (!isAuthorized) return;
    
    let subscription: any = null;
    
    switch (selectedReport) {
      case 'payments':
        subscription = supabase
          .channel('payments_changes')
          .on('postgres_changes', { 
            event: '*', 
            schema: 'public', 
            table: 'students',
            filter: 'payment_status=neq.null' 
          }, () => {
            console.log('Payment status changed, refreshing data...');
            loadReportData();
          })
          .subscribe();
        break;
        
      case 'registrations':
        subscription = supabase
          .channel('registrations_changes')
          .on('postgres_changes', { 
            event: '*', 
            schema: 'public', 
            table: 'student_event_registrations' 
          }, () => {
            console.log('Registrations changed, refreshing data...');
            loadReportData();
          })
          .subscribe();
        break;
        
      case 'refereeAssignments':
        subscription = supabase
          .channel('referee_assignments_changes')
          .on('postgres_changes', { 
            event: '*', 
            schema: 'public', 
            table: 'event_referees' 
          }, () => {
            console.log('Referee assignments changed, refreshing data...');
            loadReportData();
          })
          .subscribe();
        break;
    }
    
    // Clean up subscription on unmount or when changing report type
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, [isAuthorized, selectedReport]);

  const checkAuthorization = async () => {
    try {
      const { data: userRoles } = await supabase
        .from('user_roles')
        .select(`
          roles!inner(
            role_name
          )
        `)
        .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

      const hasAccess = userRoles?.some(ur => 
        ur.roles.role_name === 'admin' || 
        ur.roles.role_name === 'manager'
      ) ?? false;

      setIsAuthorized(hasAccess);
      if (!hasAccess) {
        navigate('/');
      }
    } catch (error) {
      console.error('Error checking authorization:', error);
      setError('Failed to verify access permissions');
    }
  };

  const loadCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('sports_categories')
        .select('*')
        .order('category_name');
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAgeGroups = async () => {
    try {
      const { data, error } = await supabase
        .from('age_groups')
        .select('*')
        .order('group_name');
      
      if (error) throw error;
      setAgeGroups(data || []);
    } catch (error) {
      console.error('Error loading age groups:', error);
    }
  };

  const loadReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      let data;
      switch (selectedReport) {
        case 'payments':
          const { data: payments } = await supabase
            .from('students')
            .select(`
              student_id,
              first_name,
              last_name,
              first_name_tamil,
              last_name_tamil,
              reference_number,
              payment_status,
              payment_date,
              payment_notes,
              department:departments(name)
            `)
            .order('payment_status', { ascending: false })
            .order('last_name');
          data = payments;
          break;

        case 'registrations':
          const { data: registrations } = await supabase
            .from('student_event_registrations')
            .select(`
              *,
              student:students(
                first_name,
                last_name,
                first_name_tamil,
                last_name_tamil,
                house:houses(house_name)
              ),
              event:events(
                event_name,
                category:sports_categories(category_name)
              )
            `)
            .order('created_at', { ascending: false });
          data = registrations;
          break;

        case 'results':
          const { data: results } = await supabase
            .from('competition_results')
            .select(`
              *,
              student:students(
                first_name,
                last_name,
                first_name_tamil,
                last_name_tamil,
                house:houses(house_name)
              ),
              event:events(
                event_id,
                event_name,
                category_id,
                age_group_id,
                category:sports_categories(
                  category_id,
                  category_name
                )
              )
            `)
            .order('created_at', { ascending: false });
          
          data = results;
          
          // Merge years found in result data with any already loaded from sports_years
          if (results && results.length > 0) {
            const fromData = results.map(r => r.competition_year).filter(Boolean) as number[];
            setCompetitionYears((prev) => {
              const merged = Array.from(new Set([...prev, ...fromData]));
              return merged.sort((a, b) => b - a);
            });
          }
          break;
          
        case 'refereeAssignments':
          const { data: assignments } = await supabase
            .from('event_referees')
            .select(`
              *,
              event:events(
                event_id,
                event_name,
                reference_number,
                status,
                competition_year,
                category:sports_categories(
                  category_id,
                  category_name
                ),
                age_group:age_groups(
                  age_group_id,
                  group_name
                )
              ),
              referee:referees(
                referee_id,
                first_name,
                last_name,
                first_name_tamil,
                last_name_tamil,
                email,
                phone,
                department:departments(
                  department_id,
                  name,
                  name_tamil
                )
              )
            `)
            .order('created_at', { ascending: false });
          data = assignments;
          break;
      }
      setReportData(data || []);
      // Reset sort when changing report type
      setSortConfig(null);
      setRefreshing(false);
    } catch (error) {
      console.error('Error loading report data:', error);
      setError('Failed to load report data');
      setRefreshing(false);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    loadReportData();
  };

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    const filteredData = getFilteredData();

    switch (format) {
      case 'excel':
        exportToExcel(filteredData);
        break;
      case 'csv':
        exportToCSV(filteredData);
        break;
      case 'pdf':
        exportToPDF(filteredData);
        break;
    }
    setShowExportMenu(false);
  };

  const getFilteredData = () => {
    let filteredData;
    
    switch (selectedReport) {
      case 'payments':
        filteredData = reportData.filter(item => {
          const searchString = searchTerm.toLowerCase();
          return (
            `${item.first_name} ${item.last_name}`.toLowerCase().includes(searchString) ||
            (item.reference_number || '').toLowerCase().includes(searchString) ||
            item.payment_status.toLowerCase().includes(searchString)
          );
        });
        break;
      
      case 'registrations':
        filteredData = reportData.filter(item => {
          const searchString = searchTerm.toLowerCase();
          const matchesSearch = (
            `${item.student?.first_name} ${item.student?.last_name}`.toLowerCase().includes(searchString) ||
            item.event?.event_name.toLowerCase().includes(searchString)
          );
          const matchesYear = selectedYear === 'All' || item.competition_year?.toString() === selectedYear;
          return matchesSearch && matchesYear;
        });
        break;
      
      case 'results':
        filteredData = reportData.filter(item => {
          const searchString = searchTerm.toLowerCase();
          const matchesSearch = (
            `${item.student?.first_name} ${item.student?.last_name}`.toLowerCase().includes(searchString) ||
            item.event?.event_name.toLowerCase().includes(searchString)
          );
          
          const matchesCategory = selectedCategory === 'All' || item.event?.category_id === selectedCategory;
          const matchesAgeGroup = selectedAgeGroup === 'All' || item.age_group_id === selectedAgeGroup;
          const matchesYear = selectedYear === 'All' || item.competition_year.toString() === selectedYear;
          
          return matchesSearch && matchesCategory && matchesAgeGroup && matchesYear;
        });
        break;
        
      case 'refereeAssignments':
        filteredData = reportData.filter(item => {
          const searchString = searchTerm.toLowerCase();
          const matchesSearch = (
            `${item.referee?.first_name || ''} ${item.referee?.last_name || ''}`.toLowerCase().includes(searchString) ||
            (item.referee?.first_name_tamil && item.referee?.last_name_tamil &&
              `${item.referee.first_name_tamil} ${item.referee.last_name_tamil}`.toLowerCase().includes(searchString)) ||
            item.event?.event_name.toLowerCase().includes(searchString) ||
            item.role.toLowerCase().includes(searchString)
          );
          const matchesYear = selectedYear === 'All' || item.event?.competition_year?.toString() === selectedYear;
          return matchesSearch && matchesYear;
        });
        break;
      
      default:
        filteredData = reportData;
    }
    
    // Apply sorting if configured
    if (sortConfig !== null) {
      filteredData.sort((a, b) => {
        // Handle nested properties with dot notation
        const getNestedProperty = (obj: any, path: string) => {
          return path.split('.').reduce((prev, curr) => {
            return prev ? prev[curr] : null;
          }, obj);
        };
        
        const aValue = getNestedProperty(a, sortConfig.key);
        const bValue = getNestedProperty(b, sortConfig.key);
        
        // Handle different data types
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        if (typeof aValue === 'string' && typeof bValue === 'string') {
          return sortConfig.direction === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        
        // For numbers, dates, etc.
        return sortConfig.direction === 'asc' 
          ? (aValue > bValue ? 1 : -1)
          : (bValue > aValue ? 1 : -1);
      });
    }
    
    return filteredData;
  };

  const requestSort = (key: string) => {
    let direction: SortDirection = 'asc';
    
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }
    
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return null;
    }
    
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-4 w-4 inline-block ml-1" /> 
      : <ArrowDown className="h-4 w-4 inline-block ml-1" />;
  };

  const renderPaymentsTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('first_name')}
          >
            {t('reports.payments.table.name')}
            {getSortIcon('first_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('reference_number')}
          >
            {t('reports.payments.table.reference')}
            {getSortIcon('reference_number')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('department.name')}
          >
            {t('reports.payments.table.department')}
            {getSortIcon('department.name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('payment_status')}
          >
            {t('reports.payments.table.status')}
            {getSortIcon('payment_status')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('payment_date')}
          >
            {t('reports.payments.table.date')}
            {getSortIcon('payment_date')}
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            {t('reports.payments.table.notes')}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {getFilteredData().map((item) => (
          <tr key={item.student_id}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">
                {item.first_name} {item.last_name}
              </div>
              <div className="text-sm text-gray-500">
                {item.first_name_tamil} {item.last_name_tamil}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.reference_number || '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.department?.name || '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                item.payment_status === 'paid' ? 'bg-green-100 text-green-800' :
                item.payment_status === 'school_paid' ? 'bg-blue-100 text-blue-800' :
                'bg-red-100 text-red-800'
              }`}>
                {t(`reports.payments.status.${item.payment_status}`)}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.payment_date ? new Date(item.payment_date).toLocaleDateString() : '-'}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.payment_notes || '-'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderRegistrationsTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('student.first_name')}
          >
            {t('reports.registrations.table.student')}
            {getSortIcon('student.first_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.event_name')}
          >
            {t('reports.registrations.table.event')}
            {getSortIcon('event.event_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.category.category_name')}
          >
            {t('reports.registrations.table.category')}
            {getSortIcon('event.category.category_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('student.house.house_name')}
          >
            {t('reports.registrations.table.house')}
            {getSortIcon('student.house.house_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('status')}
          >
            {t('reports.registrations.table.status')}
            {getSortIcon('status')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('created_at')}
          >
            {t('reports.registrations.table.date')}
            {getSortIcon('created_at')}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {getFilteredData().map((item) => (
          <tr key={item.registration_id}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">
                {item.student?.first_name} {item.student?.last_name}
              </div>
              <div className="text-sm text-gray-500">
                {item.student?.first_name_tamil} {item.student?.last_name_tamil}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {item.event?.event_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.event?.category?.category_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.student?.house?.house_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                item.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                item.status === 'withdrawn' ? 'bg-red-100 text-red-800' :
                item.status === 'disqualified' ? 'bg-gray-100 text-gray-800' :
                'bg-blue-100 text-blue-800'
              }`}>
                {t(`reports.registrations.status.${item.status}`)}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {new Date(item.created_at).toLocaleDateString()}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderResultsTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('student.first_name')}
          >
            {t('reports.results.table.student')}
            {getSortIcon('student.first_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.event_name')}
          >
            {t('reports.results.table.event')}
            {getSortIcon('event.event_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.category.category_name')}
          >
            {t('reports.results.table.category')}
            {getSortIcon('event.category.category_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('student.house.house_name')}
          >
            {t('reports.results.table.house')}
            {getSortIcon('student.house.house_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('position')}
          >
            {t('reports.results.table.position')}
            {getSortIcon('position')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('points_earned')}
          >
            {t('reports.results.table.points')}
            {getSortIcon('points_earned')}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {getFilteredData().map((item) => (
          <tr key={item.result_id}>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">
                {item.student?.first_name} {item.student?.last_name}
              </div>
              <div className="text-sm text-gray-500">
                {item.student?.first_name_tamil} {item.student?.last_name_tamil}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {item.event?.event_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.event?.category?.category_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.student?.house?.house_name}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              {item.position ? (
                <span className={`px-3 py-1 inline-flex text-sm font-medium rounded ${
                  item.position === 1 ? 'bg-yellow-100 text-yellow-800' :
                  item.position === 2 ? 'bg-gray-200 text-gray-800' :
                  item.position === 3 ? 'bg-amber-100 text-amber-800' :
                  ''
                }`}>
                  {item.position}
                </span>
              ) : (
                <span className="text-sm text-gray-500">{t('reports.results.participation')}</span>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
              {item.points_earned}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  
  const renderRefereeAssignmentsTable = () => (
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('referee.first_name')}
          >
            Referee
            {getSortIcon('referee.first_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('referee.department.name')}
          >
            Department
            {getSortIcon('referee.department.name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.event_name')}
          >
            Competition
            {getSortIcon('event.event_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.category.category_name')}
          >
            Category
            {getSortIcon('event.category.category_name')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('role')}
          >
            Role
            {getSortIcon('role')}
          </th>
          <th 
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
            onClick={() => requestSort('event.status')}
          >
            Status
            {getSortIcon('event.status')}
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {getFilteredData().map((item) => (
          <tr key={item.assignment_id} className="hover:bg-gray-50">
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm font-medium text-gray-900">
                {item.referee?.first_name || 'Unknown'} {item.referee?.last_name || 'Referee'}
              </div>
              {item.referee?.first_name_tamil && item.referee?.last_name_tamil && (
                <div className="text-sm text-gray-500">
                  {item.referee.first_name_tamil} {item.referee.last_name_tamil}
                </div>
              )}
              <div className="text-xs text-gray-500">
                {item.referee?.phone || item.referee?.email || '-'}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-900">
                {item.referee?.department?.name || '-'}
              </div>
              {item.referee?.department?.name_tamil && (
                <div className="text-sm text-gray-500">
                  {item.referee.department.name_tamil}
                </div>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-900">
                {item.event?.event_name || '-'}
              </div>
              <div className="text-xs text-gray-500">
                {item.event?.reference_number || '-'}
              </div>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <div className="text-sm text-gray-900">
                {item.event?.category?.category_name || '-'}
              </div>
              {item.event?.age_group && (
                <div className="text-xs text-gray-500">
                  {item.event.age_group.group_name}
                </div>
              )}
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.role === 'head' ? 'bg-red-100 text-red-800' :
                item.role === 'assistant' ? 'bg-blue-100 text-blue-800' :
                item.role === 'timekeeper' ? 'bg-green-100 text-green-800' :
                'bg-purple-100 text-purple-800'
              }`}>
                {item.role === 'head' ? 'Head Referee' :
                 item.role === 'assistant' ? 'Assistant' :
                 item.role === 'timekeeper' ? 'Timekeeper' : 'Scorer'}
              </span>
            </td>
            <td className="px-6 py-4 whitespace-nowrap">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                item.event?.status === 'not_started' ? 'bg-gray-100 text-gray-800' :
                item.event?.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                'bg-green-100 text-green-800'
              }`}>
                {t(`competitions.status.${item.event?.status || 'not_started'}`)}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('reports.title')}</h1>
        <div className="flex space-x-3">
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="h-4 w-4 mr-2" />
              {t('reports.actions.export')}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                <div className="py-1" role="menu">
                  <button
                    onClick={() => handleExport('excel')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    {t('reports.export.toExcel')}
                  </button>
                  <button
                    onClick={() => handleExport('csv')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    {t('reports.export.toCSV')}
                  </button>
                  <button
                    onClick={() => handleExport('pdf')}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                  >
                    <FilePdf className="h-4 w-4 mr-2" />
                    {t('reports.export.toPDF')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('reports.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
            <div className="flex-shrink-0">
              <select
                value={selectedReport}
                onChange={(e) => setSelectedReport(e.target.value as ReportType)}
                className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                <option value="payments">{t('reports.types.payments')}</option>
                <option value="registrations">{t('reports.types.registrations')}</option>
                <option value="results">{t('reports.types.results')}</option>
                <option value="refereeAssignments">Referee Assignments</option>
              </select>
            </div>
            
            {/* Additional filters for results */}
            {selectedReport === 'results' && (
              <>
                <div className="flex-shrink-0">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="All">{t('competitions.filters.allCategories')}</option>
                    {categories.map(category => (
                      <option key={category.category_id} value={category.category_id}>
                        {category.category_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex-shrink-0">
                  <select
                    value={selectedAgeGroup}
                    onChange={(e) => setSelectedAgeGroup(e.target.value)}
                    className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  >
                    <option value="All">{t('competitions.filters.allAgeGroups')}</option>
                    {ageGroups.map(ageGroup => (
                      <option key={ageGroup.age_group_id} value={ageGroup.age_group_id}>
                        {ageGroup.group_name}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {/* Year filter — shown for any report where the data has a competition_year */}
            {selectedReport !== 'payments' && (
              <div className="flex-shrink-0">
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                >
                  <option value="All">All Years</option>
                  {competitionYears.map(year => (
                    <option key={year} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* For referee assignments, add a link to the detailed report */}
            {selectedReport === 'refereeAssignments' && (
              <div className="flex-shrink-0">
                <Link
                  to="/reports/referee-assignments"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserCog className="h-4 w-4 mr-2" />
                  Detailed View
                </Link>
              </div>
            )}
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            {selectedReport === 'payments' && renderPaymentsTable()}
            {selectedReport === 'registrations' && renderRegistrationsTable()}
            {selectedReport === 'results' && renderResultsTable()}
            {selectedReport === 'refereeAssignments' && renderRefereeAssignmentsTable()}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;